import React, { useEffect, useState } from 'react'

import { GET_PRODUCTS, UPDATE_PRODUCT_PRICE, DELETE_PRODUCT_PRICE, GET_CURRENCIES } from 'api/products'
import { GET_EXCHANGE_RATE } from 'api/exchangeRates'

import Preloader from 'components/Preloader/Preloader'
import SEO from 'components/SEO/SEO'
import ConfirmationDialog from 'components/Modal/ConfirmationDialog/ConfirmationDialog'

import { DEFAULT_CURRENCY } from 'constants/currencies'

import useModal from 'hooks/useModal'

import { GroupFilter, CurrencyFilter } from './components/Filters/Filters'
import ProductsTable from './components/ProductsTable/ProductsTable'
import FeaturesModal from './components/FeaturesModal/FeaturesModal'
import AddPriceModal from './components/AddPriceModal/AddPriceModal'
import UpdatePriceModal from './components/UpdatePriceModal/UpdatePriceModal'
import AddInflatedPriceModal from './components/AddInflatedPriceModal/AddInflatedPriceModal'
import UpdateInflatedPriceModal from './components/UpdateInflatedPriceModal/UpdateInflatedPriceModal'

import { getUniqueGroups, getCurrenciesSymbol } from './utils'
import { MODALS } from './constants'

import styles from './Plans.module.scss'

const Plans = () => {
    const [isReady, setIsReady] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const [products, setProducts] = useState([])
    const [currencies, setCurrencies] = useState([])

    const { modalName, modalPayload, onOpenModal, onCloseModal } = useModal()
    const [groups, setGroups] = useState([])

    const [selectedCurrency, setSelectedCurrency] = useState(DEFAULT_CURRENCY)
    const [selectedGroup, setSelectedGroup] = useState(null)

    const [exchangeRates, setExchangeRates] = useState({})

    useEffect(() => {
        Promise.all([updateProducts(), updateCurrencies(), updateExchangeRate()])
            .then(() => setIsReady(true))
            .catch(err => console.error(err))
    }, [])

    const updateExchangeRate = async () => {
        try {
            const data = await GET_EXCHANGE_RATE(DEFAULT_CURRENCY)
            const result = {}
            for (const [key, value] of Object.entries(data[DEFAULT_CURRENCY.toLowerCase()])) {
                result[key.toUpperCase()] = value
            }
            setExchangeRates(result)
        } catch (err) {
            console.error(err)
        }
    }

    const updateProducts = async () => {
        setIsLoading(true)
        try {
            const data = await GET_PRODUCTS()
            setProducts(data)
            setGroups(getUniqueGroups(data))
        } catch (err) {
            console.error(err)
        } finally {
            setIsLoading(false)
        }
    }
    const updateCurrencies = async () => {
        setIsLoading(true)
        try {
            const data = await GET_CURRENCIES()
            setCurrencies(data)
        } catch (err) {
            console.error(err)
        } finally {
            setIsLoading(false)
        }
    }

    const onUpdatePrice = async data => {
        const { product, currency, price, isInflated } = data
        await UPDATE_PRODUCT_PRICE(product.id, {
            productCode: product.productCode,
            newPrice: price,
            currency,
            isInflatedPrice: isInflated,
        })
        await updateProducts()
    }

    const onDeletePrice = async data => {
        const { product, currency, isInflated } = data
        await DELETE_PRODUCT_PRICE(product.id, {
            productCode: product.productCode,
            currency,
            isInflatedPrice: isInflated,
        })
        await updateProducts()
    }

    return (
        <div className={styles.plans}>
            <SEO title="Plans" />
            {isReady ? (
                <>
                    <GroupFilter
                        products={products}
                        onClick={v =>
                            setSelectedGroup(prevState => {
                                if (prevState === v) return null
                                return v
                            })
                        }
                        selectedGroup={selectedGroup}
                    />
                    <br />
                    <CurrencyFilter
                        currencies={currencies}
                        onClick={v => setSelectedCurrency(v)}
                        selectedCurrency={selectedCurrency}
                    />
                    <br />
                    <br />
                    <ul className={styles.tables}>
                        {(selectedGroup ? [selectedGroup] : groups).map(group => (
                            <li key={group} className={styles.table}>
                                <ProductsTable
                                    currency={selectedCurrency}
                                    title={group}
                                    currenciesSymbol={getCurrenciesSymbol(currencies)}
                                    list={products.filter(product => product.group === group)}
                                    exchangeRates={exchangeRates}
                                    methods={{
                                        onOpenModal,
                                    }}
                                    isLoading={isLoading}
                                />
                            </li>
                        ))}
                    </ul>
                </>
            ) : (
                <Preloader />
            )}

            {modalName === MODALS.FEATURES && <FeaturesModal product={modalPayload} onClose={onCloseModal} />}
            {modalName === MODALS.ADD_PRICE && (
                <AddPriceModal
                    currenciesSymbol={getCurrenciesSymbol(currencies)}
                    payload={modalPayload}
                    onClose={onCloseModal}
                    onSubmit={onUpdatePrice}
                />
            )}
            {modalName === MODALS.EDIT_PRICE && (
                <UpdatePriceModal
                    currenciesSymbol={getCurrenciesSymbol(currencies)}
                    payload={modalPayload}
                    onClose={onCloseModal}
                    onSubmit={onUpdatePrice}
                />
            )}
            {modalName === MODALS.DELETE_PRICE && (
                <ConfirmationDialog
                    onClose={onCloseModal}
                    onAction={async () => onDeletePrice(modalPayload)}
                    targetText={`${modalPayload.product.name} (${modalPayload.product.productCode})`}
                    successText="Price removed successfully!"
                    headText="Please, confirm"
                    noteText={`Are you sure you want to remove price?`}
                    actionVariant="danger"
                    actionText="Remove"
                    cancelText="Cancel"
                />
            )}
            {modalName === MODALS.ADD_INFLATED_PRICE && (
                <AddInflatedPriceModal
                    currenciesSymbol={getCurrenciesSymbol(currencies)}
                    payload={modalPayload}
                    onClose={onCloseModal}
                    onSubmit={onUpdatePrice}
                />
            )}
            {modalName === MODALS.EDIT_INFLATED_PRICE && (
                <UpdateInflatedPriceModal
                    currenciesSymbol={getCurrenciesSymbol(currencies)}
                    payload={modalPayload}
                    onClose={onCloseModal}
                    onSubmit={onUpdatePrice}
                />
            )}
            {modalName === MODALS.DELETE_INFLATED_PRICE && (
                <ConfirmationDialog
                    onClose={onCloseModal}
                    onAction={async () => onDeletePrice(modalPayload)}
                    targetText={`${modalPayload.product.name} (${modalPayload.product.productCode})`}
                    successText="Inflated price removed successfully!"
                    headText="Please, confirm"
                    noteText={`Are you sure you want to remove inflated price?`}
                    actionVariant="danger"
                    actionText="Remove"
                    cancelText="Cancel"
                />
            )}
        </div>
    )
}

export default Plans
